import { Component } from '@angular/core';
import { MainLayout } from '../../layouts/main-layout';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  Star,
  Shield,
  Award,
  Clock,
  Wrench,
  Lightbulb,
} from 'lucide-angular';

@Component({
  selector: 'allas-wagner',
  imports: [MainLayout, CommonModule, LucideAngularModule],
  template: `
    <allas-main-layout [showPopularSections]="true" [showNewsletter]="true">
      <div class="min-h-screen bg-white">
        <!-- Hero Section -->
        <section
          class="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16"
        >
          <div class="container mx-auto px-4">
            <div class="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div
                  class="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block"
                >
                  LÍNEA PREMIUM
                </div>
                <h2 class="text-4xl lg:text-5xl font-bold mb-6">
                  WAGNER® EN ALLAS REPUESTOS
                </h2>
                <p class="text-xl mb-6 text-blue-100">
                  Innovación y calidad superior en frenos e iluminación
                  automotriz. Más de 130 años de experiencia respaldando tu
                  seguridad.
                </p>
              </div>
              <div class="relative">
                <img
                  src="/marcas/wagner/discos-wagner.png"
                  alt="Productos Wagner - Frenos"
                  class="rounded-lg shadow-none w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Nueva Línea, Misma Calidad -->
        <section class="py-16 bg-gray-50">
          <div class="container mx-auto px-4">
            <div class="grid lg:grid-cols-2 gap-12 items-center">
              <div class="relative">
                <img
                  src="/marcas/wagner/luces-wagner.png"
                  alt="Productos Wagner - Iluminación"
                  class="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-4">
                  NUEVA LÍNEA. MISMA GRAN CALIDAD.
                  <div class="w-16 h-1 bg-orange-500 mt-2"></div>
                </h2>
                <p class="text-gray-700 text-lg leading-relaxed mb-6">
                  En Allas Repuestos, nos enorgullece ofrecer la línea completa
                  Wagner®, combinando nuestra larga trayectoria en repuestos
                  automotrices con la innovación de vanguardia que mantiene a
                  Wagner® a la vanguardia de la industria. Hemos optimizado
                  nuestra oferta para brindar una experiencia simplificada que
                  facilite la selección de las piezas que mejor se adapten a tus
                  necesidades, pero ten la seguridad de que las piezas dentro
                  del empaque son los mismos productos de calidad superior que
                  has llegado a conocer y confiar.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- Productos Principales -->
        <section class="py-16">
          <div class="container mx-auto px-4">
            <h2 class="text-3xl font-bold text-center mb-12">
              LÍNEAS PRINCIPALES WAGNER®
            </h2>

            <div class="grid md:grid-cols-2 gap-8 mb-16">
              <!-- Frenos -->
              <div
                class="overflow-hidden hover:shadow-lg transition-shadow bg-white rounded-lg border border-gray-200"
              >
                <div class="p-0">
                  <div class="relative h-64">
                    <img
                      src="/marcas/wagner/discos-wagner.png"
                      alt="Sistema de Frenos Wagner"
                      class="w-full h-full object-cover"
                    />
                    <div
                      class="absolute inset-0 bg-black bg-opacity-40 flex items-end"
                    >
                      <div class="p-6 text-white">
                        <h3 class="text-2xl font-bold mb-2">
                          SISTEMAS DE FRENOS
                        </h3>
                        <p class="text-sm opacity-90">
                          Tecnología de frenado superior
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="p-6">
                    <p class="text-gray-700 mb-4">
                      Wagner® es líder en tecnologías de frenos para vehículos
                      de pasajeros, trabajo semipesado y aplicaciones
                      comerciales. Con cobertura completa para una reparación
                      integral.
                    </p>
                    <div class="flex items-center gap-4 text-sm text-gray-600">
                      <span class="flex items-center gap-1">
                        <lucide-icon
                          [name]="wrenchIcon"
                          class="w-4 h-4"
                        ></lucide-icon>
                        Pastillas y Discos
                      </span>
                      <span class="flex items-center gap-1">
                        <lucide-icon
                          [name]="shieldIcon"
                          class="w-4 h-4"
                        ></lucide-icon>
                        Líquidos DOT
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Iluminación -->
              <div
                class="overflow-hidden hover:shadow-lg transition-shadow bg-white rounded-lg border border-gray-200"
              >
                <div class="p-0">
                  <div class="relative h-64">
                    <img
                      src="/marcas/wagner/luces-wagner.png"
                      alt="Iluminación Wagner"
                      class="w-full h-full object-cover"
                    />
                    <div
                      class="absolute inset-0 bg-black bg-opacity-40 flex items-end"
                    >
                      <div class="p-6 text-white">
                        <h3 class="text-2xl font-bold mb-2">ILUMINACIÓN</h3>
                        <p class="text-sm opacity-90">
                          Visibilidad y seguridad superior
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="p-6">
                    <p class="text-gray-700 mb-4">
                      Los productos de iluminación Wagner® han estado a la
                      vanguardia desde la invención de los faros sellados.
                      Mejora la visibilidad y seguridad con cobertura total para
                      todas las necesidades.
                    </p>
                    <div class="flex items-center gap-4 text-sm text-gray-600">
                      <span class="flex items-center gap-1">
                        <lucide-icon
                          [name]="lightbulbIcon"
                          class="w-4 h-4"
                        ></lucide-icon>
                        Faros y Bombillas
                      </span>
                      <span class="flex items-center gap-1">
                        <lucide-icon
                          [name]="starIcon"
                          class="w-4 h-4"
                        ></lucide-icon>
                        Tecnología LED
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Por qué Wagner -->
        <section class="py-16">
          <div class="container mx-auto px-4">
            <div class="grid lg:grid-cols-2 gap-12 items-center">
              <div class="relative">
                <img
                  src="/marcas/wagner/discos-wagner.png"
                  alt="Calidad Wagner"
                  class="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-4">
                  ¿POR QUÉ WAGNER®?
                  <div class="w-16 h-1 bg-orange-500 mt-2"></div>
                </h2>
                <p class="text-gray-700 text-lg leading-relaxed mb-6">
                  Con más de 130 años de historia, Wagner® prospera en la
                  innovación en el corazón de cada uno de nuestros productos.
                  Nuestro futuro es aún más prometedor mientras nos esforzamos
                  por cambiar la industria manteniéndonos más fuertes. Juntos,
                  nuestro extenso portafolio de piezas presenta productos de
                  alta calidad diseñados para vehículos GM así como muchas otras
                  marcas y modelos.
                </p>
                <div class="grid grid-cols-1 gap-4">
                  <div class="flex items-start gap-3">
                    <lucide-icon
                      [name]="clockIcon"
                      class="w-6 h-6 text-orange-500 mt-1 flex-shrink-0"
                    ></lucide-icon>
                    <div>
                      <h4 class="font-semibold mb-1">Experiencia Comprobada</h4>
                      <p class="text-gray-600 text-sm">
                        Más de 130 años innovando en la industria automotriz
                      </p>
                    </div>
                  </div>
                  <div class="flex items-start gap-3">
                    <lucide-icon
                      [name]="awardIcon"
                      class="w-6 h-6 text-orange-500 mt-1 flex-shrink-0"
                    ></lucide-icon>
                    <div>
                      <h4 class="font-semibold mb-1">Cobertura Completa</h4>
                      <p class="text-gray-600 text-sm">
                        Soluciones integrales para todas las necesidades de
                        reparación
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- CTA Final -->
        <section class="py-16 bg-red-600 text-white">
          <div class="container mx-auto px-4 text-center">
            <h2 class="text-3xl font-bold mb-4">
              ENCUENTRA WAGNER® EN ALLAS REPUESTOS
            </h2>
            <p class="text-xl mb-8 text-red-100">
              Distribuidor autorizado con stock completo y asesoramiento
              especializado
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/encuentra-tu-tienda"
                class="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg transition-colors inline-block"
              >
                Encuentra tu Tienda
              </a>
            </div>
          </div>
        </section>
      </div>
    </allas-main-layout>
  `,
})
export class Wagner {
  // Iconos para Lucide Angular
  starIcon = Star;
  shieldIcon = Shield;
  awardIcon = Award;
  clockIcon = Clock;
  wrenchIcon = Wrench;
  lightbulbIcon = Lightbulb;
}
